import { Component } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { finalize } from 'rxjs';
import { Constant } from 'src/app/services/auth/Constant';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { SuccessModalComponent } from 'src/app/shared/modals/success-modal/success-modal.component';
import { ErrorModalComponent } from 'src/app/shared/modals/error-modal/error-modal.component';
import { ConfirmActionComponent } from 'src/app/shared/components/confirm-action/confirm-action.component';
import { ApiService } from 'src/app/services/api/api.service';


@Component({
  selector: 'app-create-password',
  templateUrl: './create-password.component.html',
  styleUrl: './create-password.component.scss',
  standalone: false,
})
export class CreatePasswordComponent {
  createPasswordForm: FormGroup;
  errorMessage: string;
  passwordCreationInProgress: boolean;
  showPassword: boolean = false;

  showCaptcha: boolean = false;
  captchaPassed: boolean = false;
  countdown = 300;
  rotationAngle = 0;
  private intervalId: any;
  expiryTime: string = '';
  remainingTime: string = '';

  constructor(
    private formBuilder: FormBuilder,
    private dialog: MatDialog,
    private apiService: ApiService,
    private router: Router,
  ) { }

  ngOnInit(): void {

    this.initForm();

    const isRefreshed = sessionStorage.getItem('firstLoad');

    if (isRefreshed == 'true') {
      this.router.navigate(['/login']);
      return;
    }

    sessionStorage.setItem('firstLoad', 'true');

    this.calculateExpiryTime();
    this.startCountdown();

    this.createPasswordForm.valueChanges.subscribe(() => {
      this.errorMessage = null;
    });

    sessionStorage.setItem('firstLoad', 'true');

  }

  initForm() {
    this.createPasswordForm = this.formBuilder.group({
      password: ['', [Validators.required, Validators.maxLength(50)]],
      confirmPassword: ['', [Validators.required, Validators.maxLength(50)]],
    },
      { validators: this.passwordGroupValidator }
    );
  }



  setExpiryTime() {
    const now = new Date();
    now.setMinutes(now.getMinutes() + 5);
    this.expiryTime = now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  startCountdown() {
    this.intervalId = setInterval(() => {
      if (this.countdown > 0) {
        this.countdown--;
        this.rotationAngle = (this.countdown / 300) * 360;
        this.updateRemainingTime();
      } else {
        clearInterval(this.intervalId);
        window.location.href = '/login';
      }
    }, 1000);
  }

  calculateExpiryTime() {
    const now = new Date();
    now.setMinutes(now.getMinutes() + 5);

    const hours = now.getHours() % 12 || 12;
    const minutes = now.getMinutes().toString().padStart(2, '0');
    const amPm = now.getHours() >= 12 ? 'PM' : 'AM';

    this.expiryTime = `${hours}:${minutes} ${amPm}`;
    this.updateRemainingTime();
  }

  updateRemainingTime() {
    const minutes = Math.floor(this.countdown / 60).toString().padStart(2, '0');
    const seconds = (this.countdown % 60).toString().padStart(2, '0');
    this.remainingTime = `${minutes}:${seconds}`;
  }


  getUsedArc(): string {
    const endAngle = (300 - this.countdown) * (360 / 300);
    return this.describeArc(50, 50, 40, 0, endAngle);
  }

  getRemainingArc(): string {
    const startAngle = (300 - this.countdown) * (360 / 300);
    return this.describeArc(50, 50, 40, startAngle, 360);
  }

  describeArc(x: number, y: number, radius: number, startAngle: number, endAngle: number): string {
    const start = this.polarToCartesian(x, y, radius, endAngle);
    const end = this.polarToCartesian(x, y, radius, startAngle);
    const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";

    return `M ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArcFlag} 0 ${end.x} ${end.y}`;
  }

  polarToCartesian(centerX: number, centerY: number, radius: number, angleInDegrees: number) {
    const angleInRadians = (angleInDegrees - 90) * (Math.PI / 180);
    return {
      x: centerX + radius * Math.cos(angleInRadians),
      y: centerY + radius * Math.sin(angleInRadians),
    };
  }


  ngOnDestroy() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
  }


  toggleShowPassword() {
    this.showPassword = !this.showPassword;
  }

  get passwordControl() {
    return this.createPasswordForm?.get('password')! as FormControl
  }

  get passwordStrength(): number {
    const errors = this.createPasswordForm?.errors || {};
    return 4 - Object.keys(errors).length;
  }

  attemptLogin() {
    this.errorMessage = null;
    if (this.createPasswordForm.valid) {
      this.showCaptcha = true;
    }
  }

  togglePassword() {
    var passwordInput = document.getElementById('password') as HTMLInputElement;
    var togglePassword = this.createPasswordForm.controls['togglePassword'].value;

    if (togglePassword === true) {
      passwordInput.type = 'text';
      this.showPassword = true;
    } else {
      passwordInput.type = 'password';
      this.showPassword = false;
    }
  }

  submitForm() {
    const formData = this.createPasswordForm.value;

    this.passwordCreationInProgress = true;

    const request = {
      newPassword: formData.password,
      confirmPassword: formData.confirmPassword,
    };

      this.apiService
        .post('/auth/user/change-password', request)
        .pipe(
          finalize(() => {
            this.passwordCreationInProgress = false;
          })
        )
        .subscribe(
          (response: any) => {

            this.openSuccessModal('Password updated successfuly');
          },
          (error) => {
            this.openErrorModal(error?.message)
          }
        );

  }

  passwordGroupValidator(group: AbstractControl): ValidationErrors | null {
    const password = group.get('password')?.value || '';
    const confirmPassword = group.get('confirmPassword')?.value || '';
    const errors: ValidationErrors = {};

    if (!/[A-Z]/.test(password)) errors.hasUppercase = true;
    if (!/\d/.test(password)) errors.hasNumber = true;
    if (password.length < 8) errors.minLength8 = true;
    if (!(password || confirmPassword) || password !== confirmPassword) errors.passwordsMismatch = true;

    return Object.keys(errors).length ? errors : null;
  }

  openSuccessModal(message: string) {
    let dialogRef = this.dialog.open(SuccessModalComponent, {
      width: '350px',
      backdropClass: 'modal-form-backdrop',
      hasBackdrop: true,
      panelClass: ['modal-form', 'modal-no-overflow-changed'],
      data: { successMessage: message },
    });

    dialogRef.afterClosed().subscribe(() => {
      localStorage.removeItem(Constant.TEMP_LOGGED_IN_USER);
      this.router.navigate(["/login"]);
    });
  }


  openErrorModal(message: string) {
    let dialogRef = this.dialog.open(ErrorModalComponent, {
      width: '350px',
      backdropClass: 'modal-form-backdrop',
      hasBackdrop: true,
      panelClass: ['modal-form', 'modal-no-overflow-changed'],
      data: { errorMessage: message },
    });

    dialogRef.afterClosed().subscribe(() => {

    });
  }


  openConfirmModal() {

        this.dialog.open(ConfirmActionComponent, {
          width: '400px',
          backdropClass: 'modal-form-backdrop',
          hasBackdrop: true,
          panelClass: ['modal-form', 'modal-no-overflow-changed'],
          disableClose: true,

          data: {
            title: "Update password?",
            body: '',
            confirmText: 'Yes',
            lottieIconPath: 'assets/lottie/alert.json',
            onConfirm: () => {

              this.submitForm()
            },
            onCancel: () => {

            },
            showCancelButton: true,
            showConfirmButton: true,
          },
        });

  }


}
